<template>
  <b-modal
    :visible="visible"
    centered
    hide-header
    hide-footer
    modal-class="cmi"
    content-class="cmi__content"
    body-class="p-0"
    size="sm"
    @hidden="$emit('close')"
    @show="load"
  >
    <div class="cmi__box">
      <header class="cmi__head">
        <button
          type="button"
          class="cmi__close"
          :aria-label="$t('chat.cancel')"
          @click="$emit('close')"
        >
          <feather-icon
            icon="XIcon"
            size="20"
          />
        </button>
        <strong>{{ $t('chat.info.title') }}</strong>
      </header>

      <div class="cmi__preview">
        <chat-message-bubble
          v-if="message"
          :message="message"
          mine
          selecting
          :peer-read-id="readId"
          :peer-delivered-id="deliveredId"
        />
      </div>

      <div class="cmi__rows">
        <div
          v-for="row in rows"
          :key="row.key"
          class="cmi__row"
          :class="{ 'cmi__row--pending': !row.done }"
        >
          <span
            class="cmi__badge"
            :class="`cmi__badge--${row.key}`"
          >
            <svg
              width="18"
              height="14"
              viewBox="0 0 17 13"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            ><path d="M1 7l3.5 3.5L11 3" /><path d="M7 10.5L9 12.5 15.5 3" /></svg>
          </span>
          <span class="cmi__text">
            <strong>{{ row.label }}</strong>
            <small>{{ row.when }}</small>
          </span>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import { dayLabel, timeLabel } from '@/utils/chat'
import ChatMessageBubble from './ChatMessageBubble.vue'

export default {
  name: 'ChatMessageInfo',

  components: { ChatMessageBubble },

  props: {
    visible: { type: Boolean, default: false },
    message: { type: Object, default: null },
  },

  data() {
    return { info: { delivered_at: null, read_at: null }, loading: false }
  },

  computed: {
    // Sem a hora gravada (mensagem antiga) o estado ainda vale: usa o que a conversa já sabe do outro lado.
    thread() {
      return this.message ? this.$store.state.chat.threads[this.message.conversation_id] : null
    },
    readId() {
      return this.thread ? this.thread.peerReadId : 0
    },
    deliveredId() {
      return this.thread ? this.thread.peerDeliveredId : 0
    },
    rows() {
      const id = this.message ? this.message.id : 0
      const read = !!this.info.read_at || (!!id && id <= this.readId)
      const delivered = read || !!this.info.delivered_at || (!!id && id <= this.deliveredId)

      return [
        {
          key: 'read', label: this.$t('chat.read'), done: read, when: this.whenLabel(this.info.read_at, read, 'pendingRead'),
        },
        {
          key: 'delivered', label: this.$t('chat.delivered'), done: delivered, when: this.whenLabel(this.info.delivered_at, delivered, 'pendingDelivered'),
        },
      ]
    },
  },

  methods: {
    async load() {
      this.info = { delivered_at: null, read_at: null }
      if (!this.message || !this.message.id) return
      this.loading = true
      try {
        const { data } = await axios.get(`${this.$store.state.api}chat/messages/${this.message.id}/info`)
        this.info = data.data
      } catch (e) {
        // Sem a hora, as linhas mostram o traço.
      } finally {
        this.loading = false
      }
    },

    // Hora gravada; senão o estado ("ainda não lida") ou, em mensagem antiga que já foi lida, que a hora não existe.
    whenLabel(iso, reached, pendingKey) {
      if (!iso) {
        if (this.loading) return ''

        return reached ? this.$t('chat.info.noTime') : this.$t(`chat.info.${pendingKey}`)
      }
      const labels = { today: this.$t('chat.today'), yesterday: this.$t('chat.yesterday') }

      return this.$t('chat.info.at', { day: dayLabel(iso, this.$i18n.locale, labels), time: timeLabel(iso, this.$i18n.locale) })
    },
  },
}
</script>

<!-- Modal fora do chat (no body): estilo global e com as variáveis que a bolha usa. -->
<style>
.cmi__content { overflow: hidden; border: 0; border-radius: 16px; box-shadow: 0 18px 48px rgba(34, 41, 47, 0.28); }

.cmi__box {
  --chat-in: #fff;
  --chat-out: #e4e1fd;
  --chat-text: #3b3a4a;
  --chat-muted: #7d7b8c;
  color: #3b3a4a;
  background: #fff;
}

.cmi__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #ebe9f1;
}

.cmi__head strong { font-size: 1.05rem; font-weight: 600; }

.cmi__close {
  display: inline-flex;
  padding: 5px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #7d7b8c;
  transition: background 0.12s;
}

.cmi__close:hover { background: rgba(115, 103, 240, 0.12); color: #7367f0; }

.cmi__preview {
  padding: 22px 22px;
  background: #efedf8 radial-gradient(rgba(115, 103, 240, 0.09) 1.2px, transparent 1.2px) 0 0 / 22px 22px;
}

.cmi__rows { display: flex; flex-direction: column; gap: 10px; padding: 18px; }

.cmi__row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
  background: #faf9fd;
}

.cmi__badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(125, 123, 140, 0.14);
  color: #7d7b8c;
}

.cmi__badge--read { background: rgba(52, 183, 241, 0.16); color: #1fa4e6; }

.cmi__text { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; }
.cmi__text strong { font-size: 0.98rem; font-weight: 600; }
.cmi__text small { color: #7d7b8c; font-size: 0.84rem; }

/* Ainda não aconteceu: apagado, para o olho ir para o que já aconteceu. */
.cmi__row--pending .cmi__badge { background: rgba(125, 123, 140, 0.1); color: #b9b9c3; }
.cmi__row--pending .cmi__text strong { color: #7d7b8c; font-weight: 500; }

.dark-layout .cmi__box {
  --chat-in: #343d55;
  --chat-out: #4a3fb8;
  --chat-text: #d0d2d6;
  --chat-muted: #9a9cae;
  color: #d0d2d6;
  background: #283046;
}

.dark-layout .cmi__head { border-color: #3b4253; }
.dark-layout .cmi__preview { background-color: #1f2639; }
.dark-layout .cmi__row { border-color: #3b4253; background: #2d3550; }
.dark-layout .cmi__text small { color: #9a9cae; }
</style>
